<template>
  <section class="custom-access-requests">
    <!-- the listing gives way to the detail view, the way the other detail pages in this app work -->
    <template v-if="!detailId">
      <div class="row align-items-center">
        <div class="col">
          <h4 class="mb-1">ADAS Map Mobile access requests</h4>
          <p class="text-muted mb-0">
            Requests sent from the app's Request Access form. Link each one to the requester's Launchpad user, set up billing in NetSuite, then
            approve to turn on their access.
          </p>
        </div>
        <div class="col-4">
          <z-message v-model="message" :timeout="7000" class="alert alert-warning mb-0" />
        </div>
        <div class="col-auto">
          <router-link to="/app-usage" class="btn btn-sm btn-outline-secondary me-2">Usage &amp; credits</router-link>
          <router-link to="/app-pricing" class="btn btn-sm btn-outline-secondary">App pricing</router-link>
        </div>
      </div>
      <!-- Filters -->
      <div class="row mt-3 align-items-center">
        <div class="col-auto">
          <div class="btn-group btn-group-sm" role="group" aria-label="Filter by status">
            <button
              v-for="filter of filters"
              :key="filter.value"
              type="button"
              class="btn"
              :class="filterStatus === filter.value ? 'btn-primary' : 'btn-outline-primary'"
              @click="setStatus(filter.value)"
            >
              {{ filter.name }}
              <span class="badge rounded-pill ms-1" :class="filterStatus === filter.value ? 'bg-light text-dark' : 'bg-secondary'">{{
                countFor(filter.value)
              }}</span>
            </button>
          </div>
        </div>
        <div class="col">
          <div class="input-group input-group-sm">
            <span class="input-group-text"><i class="fa-solid fa-magnifying-glass"></i></span>
            <input
              v-model="search"
              type="search"
              class="form-control"
              placeholder="Search name, email, company, username or NetSuite reference"
              aria-label="Search access requests"
            />
          </div>
        </div>
        <div class="col-auto">
          <button class="btn btn-outline-secondary btn-sm" type="button" @click="fetch">Refresh</button>
        </div>
      </div>
      <!-- Listing -->
      <div class="row mt-2">
        <div class="col">
          <div v-show="loading.requests" class="custom-spinner">
            <FontAwesomeIcon icon="fa-solid fa-spinner" spin />
          </div>
          <div v-show="!loading.requests">
            <z-table :model-value="requests.items" class="table table-bordered table-striped table-hover" :columns="tableColumns">
              <template #tbody="props">
                <td class="custom-column-actions text-center">
                  <button
                    class="btn btn-sm"
                    type="button"
                    title="Open request"
                    :aria-label="`Open request from ${fullName(props.row)}`"
                    @click="open(props.row.id)"
                  >
                    <i class="far fa-edit"></i>
                  </button>
                </td>
                <td>
                  {{ formatDate(props.row.lastSubmittedOn)
                  }}<span v-if="props.row.submitCount > 1" class="text-muted small"> ({{ props.row.submitCount }}x)</span>
                </td>
                <td>{{ fullName(props.row) }}</td>
                <td>{{ props.row.companyName }}</td>
                <td>{{ props.row.email }}</td>
                <td>
                  {{ props.row.linkedUsername || props.row.requestedUsername
                  }}<span v-if="!props.row.linkedUsername && props.row.requestedUsername" class="text-muted small"> (requested)</span>
                </td>
                <td>{{ props.row.netsuiteReference }}</td>
                <td>
                  <span class="badge" :class="statusOf(props.row.status).badge">{{ statusOf(props.row.status).name }}</span>
                </td>
              </template>
            </z-table>
            <p v-if="!requests.items.length" class="text-muted text-center py-3">No access requests here.</p>
          </div>
        </div>
      </div>
      <!-- Paging -->
      <div v-if="pages > 1" class="row align-items-center">
        <div class="col"></div>
        <div class="col-auto">
          <nav class="custom-pagination">
            <z-paginate v-model="page" :page-count="pages" />
          </nav>
        </div>
        <div class="col-auto">{{ requests.count }} requests</div>
      </div>
    </template>
    <!-- Detail -->
    <access-request-detail v-if="detailId" :request-id="detailId" @closing="close" />
  </section>
</template>
<script>
import { mapState, mapActions } from 'pinia'
import { accessRequestStore } from '@/stores/accessRequest'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import moment from 'moment'
import _ from 'lodash'
import ZTable from '@/components/utilities/elements/Table.vue'
import ZMessage from '@/components/utilities/elements/Message.vue'
import ZPaginate from '@/components/utilities/elements/Paginate.vue'
import AccessRequestDetail from '@/components/access-requests/AccessRequestDetail.vue'
import { accessRequestStatuses, getAccessRequestStatus, OPEN_STATUSES } from '@/plugins/access-request-status'

const PAGE_SIZE = 25

export default {
  name: 'AccessRequestListing',
  components: {
    FontAwesomeIcon,
    ZTable,
    ZMessage,
    ZPaginate,
    AccessRequestDetail
  },
  data() {
    return {
      filterStatus: 'Open',
      search: '',
      page: 1,
      message: null,
      // the request shown in the detail view, which replaces this listing; ?id= opens one directly (the reviewer email links that way)
      detailId: null
    }
  },
  computed: {
    ...mapState(accessRequestStore, ['requests', 'loading']),
    filters() {
      return [{ value: 'Open', name: 'Open' }, ...accessRequestStatuses.map((s) => ({ value: s.value, name: s.name })), { value: '', name: 'All' }]
    },
    tableColumns() {
      return [
        { field: 'id', label: '' },
        { field: 'lastSubmittedOn', label: 'Submitted' },
        { field: 'name', label: 'Name' },
        { field: 'companyName', label: 'Company' },
        { field: 'email', label: 'Email' },
        { field: 'username', label: 'Launchpad user' },
        { field: 'netsuiteReference', label: 'NetSuite ref' },
        { field: 'status', label: 'Status' }
      ]
    },
    pages() {
      return Math.max(1, Math.ceil((this.requests.count || 0) / PAGE_SIZE))
    }
  },
  watch: {
    page() {
      this.fetch()
    },
    search: _.debounce(function () {
      this.page = 1
      this.fetch()
    }, 400),
    '$route.query.id'(id) {
      this.detailId = id ? parseInt(id) : null
    }
  },
  async mounted() {
    if (this.$route.query.id) {
      this.detailId = parseInt(this.$route.query.id)
    }
    await this.fetch()
  },
  methods: {
    ...mapActions(accessRequestStore, ['getRequests']),
    async fetch() {
      try {
        await this.getRequests({ status: this.filterStatus, search: this.search, page: this.page, pageSize: PAGE_SIZE })
      } catch (error) {
        this.message = `Could not load access requests: ${error}`
      }
    },
    setStatus(value) {
      this.filterStatus = value
      this.page = 1
      this.fetch()
    },
    countFor(value) {
      const counts = this.requests.statusCounts || []
      const wanted = value === 'Open' ? OPEN_STATUSES : value ? [value] : null
      return counts.filter((c) => !wanted || wanted.includes(c.status)).reduce((sum, c) => sum + c.count, 0)
    },
    statusOf(value) {
      return getAccessRequestStatus(value)
    },
    fullName(row) {
      return `${row.firstName || ''} ${row.lastName || ''}`.trim()
    },
    formatDate(value) {
      return value ? moment(value).format('MM/DD/YYYY h:mm A') : ''
    },
    open(id) {
      this.$router.push({ path: '/access-requests', query: { id } })
    },
    close() {
      this.$router.push({ path: '/access-requests' })
      this.fetch()
    }
  }
}
</script>
<style lang="scss" scoped>
.custom-spinner {
  text-align: center;
  padding: 2rem;
  font-size: 1.5rem;
}
.custom-column-actions {
  width: 48px;
}
</style>
